import React, { useContext, useEffect, useState } from 'react'
import {
    Button,
    Card,
    CardBody,
    Col,
    CustomInput,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Table
} from 'reactstrap'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import ApiService from '../../../data/services/ApiService'
import { GeneralContext } from '../../../context/'

const emptyForm = {
    id: '',
    code: '',
    name: '',
    description: '',
    color: '',
    image: '',
    renderMode: 'solid',
    frameWidth: 15,
    insetTop: 15,
    insetRight: 15,
    insetBottom: 15,
    insetLeft: 15,
    isActive: true
}

const toPositiveNumber = (value, fallback = 15) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback
}

const FramesManager = () => {
    const { setLoading } = useContext(GeneralContext)
    const [frames, setFrames] = useState([])
    const [form, setForm] = useState(emptyForm)
    const [file, setFile] = useState()

    useEffect(() => {
        fetchFrames()
    }, []) // eslint-disable-line

    const fetchFrames = async () => {
        try {
            setLoading(true)
            const response = await ApiService.getFrames()
            setFrames(response.data.frames || [])
        } catch (error) {
            toastr.error('No pude cargar los marcos', 'Error')
        } finally {
            setLoading(false)
        }
    }

    const resetForm = () => {
        setForm(emptyForm)
        setFile()
    }

    const handleFileUpload = (event) => {
        const selectedFile = event.target.files[0]
        if (!selectedFile) return

        const types = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
        if (types.every(type => selectedFile.type !== type)) {
            toastr.error('Solo se aceptan imágenes', 'Error')
            return
        }

        setFile(selectedFile)
    }

    const saveFrame = async (event) => {
        event.preventDefault()

        if (!form.code || !form.name) {
            toastr.error('Agrega código y nombre para el marco', 'Error')
            return
        }

        try {
            setLoading(true)
            let payload = {
                code: form.code,
                name: form.name,
                description: form.description,
                color: form.color || null,
                image: form.image || null,
                renderMode: form.renderMode || (form.image ? 'texture' : 'solid'),
                frameWidth: toPositiveNumber(form.frameWidth),
                insetTop: toPositiveNumber(form.insetTop),
                insetRight: toPositiveNumber(form.insetRight),
                insetBottom: toPositiveNumber(form.insetBottom),
                insetLeft: toPositiveNumber(form.insetLeft),
                isActive: form.isActive
            }

            if (file) {
                const data = new FormData()
                data.append('file', file)
                const upload = await ApiService.uploadFrameImage(data)
                payload.image = upload.data.url
                payload.color = null
                payload.renderMode = form.renderMode === 'overlay' ? 'overlay' : 'texture'
            }

            if (form.id) {
                await ApiService.updateFrame(form.id, payload)
                toastr.success('Marco actualizado', 'Éxito')
            } else {
                await ApiService.createFrame(payload)
                toastr.success('Marco agregado', 'Éxito')
            }

            resetForm()
            await fetchFrames()
        } catch (error) {
            toastr.error('No pude guardar el marco', 'Error')
        } finally {
            setLoading(false)
        }
    }

    const editFrame = (frame) => {
        setForm({
            ...emptyForm,
            ...frame,
            color: frame.color || '',
            image: frame.image || '',
            renderMode: frame.renderMode || (frame.image ? 'texture' : 'solid'),
            frameWidth: frame.frameWidth || 15,
            insetTop: frame.insetTop || 15,
            insetRight: frame.insetRight || 15,
            insetBottom: frame.insetBottom || 15,
            insetLeft: frame.insetLeft || 15
        })
        setFile()
    }

    const deleteFrame = async (id) => {
        try {
            setLoading(true)
            await ApiService.deleteFrame(id)
            toastr.success('Marco eliminado', 'Éxito')
            await fetchFrames()
        } catch (error) {
            toastr.error('No pude eliminar el marco', 'Error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Row>
            <Col md={4} xs={12}>
                <Card>
                    <CardBody>
                        <Form onSubmit={saveFrame}>
                            <FormGroup>
                                <Label>Código *</Label>
                                <Input value={form.code} onChange={event => setForm({ ...form, code: event.target.value })} />
                            </FormGroup>

                            <FormGroup>
                                <Label>Nombre *</Label>
                                <Input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} />
                            </FormGroup>

                            <FormGroup>
                                <Label>Descripción</Label>
                                <Input value={form.description || ''} onChange={event => setForm({ ...form, description: event.target.value })} />
                            </FormGroup>

                            <FormGroup>
                                <Label>Color</Label>
                                <Input value={form.color || ''} placeholder="#000000" onChange={event => setForm({ ...form, color: event.target.value, image: event.target.value ? '' : form.image, renderMode: event.target.value ? 'solid' : form.renderMode })} />
                            </FormGroup>

                            <FormGroup>
                                <Label>Modo de render</Label>
                                <Input type="select" value={form.renderMode} onChange={event => setForm({ ...form, renderMode: event.target.value, color: event.target.value === 'solid' ? form.color : '' })}>
                                    <option value="solid">Color sólido</option>
                                    <option value="texture">Textura repetida</option>
                                    <option value="overlay">Imagen completa con centro transparente</option>
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label>Ancho del marco</Label>
                                <Input type="number" min="0" value={form.frameWidth} onChange={event => setForm({ ...form, frameWidth: event.target.value })} />
                            </FormGroup>

                            <FormGroup>
                                <Label>Imagen</Label>
                                <Input type="file" onChange={handleFileUpload} />
                            </FormGroup>

                            {form.renderMode === 'overlay' && (
                                <React.Fragment>
                                    <Label>Insets de apertura</Label>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Arriba</Label>
                                                <Input type="number" min="0" value={form.insetTop} onChange={event => setForm({ ...form, insetTop: event.target.value })} />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Derecha</Label>
                                                <Input type="number" min="0" value={form.insetRight} onChange={event => setForm({ ...form, insetRight: event.target.value })} />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Abajo</Label>
                                                <Input type="number" min="0" value={form.insetBottom} onChange={event => setForm({ ...form, insetBottom: event.target.value })} />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Izquierda</Label>
                                                <Input type="number" min="0" value={form.insetLeft} onChange={event => setForm({ ...form, insetLeft: event.target.value })} />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </React.Fragment>
                            )}

                            {form.image && (
                                <FormGroup>
                                    <img alt={form.name} src={form.image} style={{ maxWidth: '100%' }} />
                                </FormGroup>
                            )}

                            <CustomInput
                                type="switch"
                                id="frame-active"
                                label="Activo"
                                checked={form.isActive}
                                onChange={() => setForm({ ...form, isActive: !form.isActive })}
                            />

                            <Button color="primary" className="mt-3" type="submit">
                                <i className="mdi mdi-check"></i> Guardar
                            </Button>

                            {form.id && (
                                <Button className="mt-3 ml-2" onClick={resetForm}>
                                    Cancelar
                                </Button>
                            )}
                        </Form>
                    </CardBody>
                </Card>
            </Col>

            <Col md={8} xs={12}>
                <Card>
                    <div className="table-responsive">
                        <Table striped className="align-middle">
                            <thead>
                                <tr>
                                    <th>Marco</th>
                                    <th>Código</th>
                                    <th>Nombre</th>
                                    <th>Modo</th>
                                    <th>Ancho</th>
                                    <th>Activo</th>
                                    <th>...</th>
                                </tr>
                            </thead>
                            <tbody>
                                {frames.map(frame => (
                                    <tr key={frame.id}>
                                        <td>
                                            {frame.image && <img alt={frame.name} src={frame.image} style={{ maxWidth: '96px' }} />}
                                            {!frame.image && frame.color && <span style={{ display: 'inline-block', width: 48, height: 32, backgroundColor: frame.color, border: '1px solid #ddd' }} />}
                                        </td>
                                        <td>{frame.code}</td>
                                        <td>{frame.name}</td>
                                        <td>{frame.renderMode || (frame.image ? 'texture' : 'solid')}</td>
                                        <td>{frame.frameWidth || 15}px</td>
                                        <td>{frame.isActive ? 'Sí' : 'No'}</td>
                                        <td>
                                            <Button size="sm" onClick={() => editFrame(frame)}>
                                                <i className="mdi mdi-pencil"></i>
                                            </Button>
                                            <Button size="sm" className="ml-2" onClick={() => deleteFrame(frame.id)}>
                                                <i className="mdi mdi-trash-can"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            </Col>
        </Row>
    )
}

export default FramesManager
